import { Button, Input, Modal, Table } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import DatePickerComponent from "../../Common/Components/DatePickerComponent";
import PermissionCheck from "../../Common/PermissionCheck";
import { PermissionOperations } from "~enums/permissions";
import { useEntityService } from "~pages/Entities/hooks/useEntityService.ts";
import { useNotification } from "~hooks/useNotification";

const NameComponent = ({ value }) => {
  return <>{value}</>;
};

const EditButton = ({ historicalNames, fetchData, row }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <>
      <AddHistoricNameModal
        historicalNames={historicalNames}
        fetchData={fetchData}
        defaultName={row.nameFullLocal}
        defaultStartDate={dayjs(row.dateStart)}
        defaultEndDate={dayjs(row.dateEnd)}
        isAddModalOpen={isModalOpen}
        setIsAddModalOpen={setIsModalOpen}
        isEditMode={true}
      />
      <Button
        outline
        color="primary"
        size="sm"
        onClick={() => {
          setIsModalOpen(true);
        }}
      >
        <i className="fas fa-pen"/>
      </Button>
    </>
  );
};

const columns = (historicalNames, fetchData) => [
  {
    title: "Team Name",
    dataIndex: "nameFullLocal",
    key: "nameFullLocal",
    render: (value) => <NameComponent value={value}/>,
  },
  {
    title: "Start Date",
    dataIndex: "dateStart",
    key: "dateStart",
    render: (value) => <div>{dayjs(value).format("DD-MM-YYYY")}</div>,
  },
  {
    title: "End Date",
    dataIndex: "dateEnd",
    key: "dateEnd",
    render: (value) => <div>{dayjs(value).format("DD-MM-YYYY")}</div>,
  },
  {
    render: (_, row) => <EditButton historicalNames={historicalNames} fetchData={fetchData} row={row}/>,
  },
];

const ModalBody = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const AddHistoricNameModal = ({
                                historicalNames,
                                fetchData,
                                defaultName = "",
                                defaultStartDate = null,
                                defaultEndDate = null,
                                isAddModalOpen,
                                setIsAddModalOpen,
                                isEditMode = false,
                              }) => {
  const { formatMessage } = useIntl();

  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const notification = useNotification();


  useEffect(() => {
    setName(defaultName);
    if (defaultStartDate) {
      setStartDate(defaultStartDate);
    }
    if (defaultEndDate) {
      setEndDate(defaultEndDate);
    }
  }, [defaultName, defaultStartDate, defaultEndDate]);

  const { entityId } = useParams();
  const { updateEntity } = useEntityService();

  const handleOk = useCallback(async () => {
    if (endDate.isBefore(startDate)) {
      notification.error({
        formatMessage: { id: "date.error.endShouldBeAfterStart" },
      })
      return;
    }
    if (!name) {
      notification.error({
        formatMessage: { id: "name.is.required" },
      })
      return;
    }
    let data = [];
    const histNames = historicalNames || [];
    const dateStart = startDate.format("YYYY-MM-DD");
    const dateEnd = endDate.format("YYYY-MM-DD");
    if (!isEditMode) {
      data = [...histNames, { nameFullLocal: name, dateStart, dateEnd }];
    } else {
      data = histNames.map((item) =>
        item.nameFullLocal === defaultName &&
        dayjs(item.dateStart).isSame(defaultStartDate) &&
        dayjs(item.dateEnd).isSame(defaultEndDate)
          ? {
            nameFullLocal: name,
            dateStart,
            dateEnd,
          }
          : item,
      );
    }
    const result = await updateEntity(entityId, { historicalNames: data });

    if (!result) {
      return;
    }

    await fetchData();
    setIsAddModalOpen(false);
  }, [
    entityId,
    updateEntity,
    historicalNames,
    startDate,
    endDate,
    name,
    defaultStartDate,
    defaultEndDate,
    defaultName,
    fetchData,
    formatMessage,
    isEditMode,
    setIsAddModalOpen
  ]);

  return (
    <>
      <Modal
        open={isAddModalOpen}
        onCancel={() => {
          setIsAddModalOpen(false);
        }}
        onOk={handleOk}
      >
        <ModalBody>
          <label htmlFor={"historicalName"}>{formatMessage({ id: "teamname" })}: </label>
          <Input
            id={"historicalName"}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <label htmlFor={"historicalNameStartDate"}>{formatMessage({ id: "date.start" })}: </label>
          <DatePickerComponent
            id={"historicalNameStartDate"}
            defaultValue={startDate}
            handleChange={(value) => {
              setStartDate(value);
            }}
          />
          <label htmlFor={"historicalNameEndDate"}>{formatMessage({ id: "date.end" })}: </label>
          <DatePickerComponent
            id={"historicalNameEndDate"}
            defaultValue={endDate}
            handleChange={(value) => {
              setEndDate(value);
            }}
          />
        </ModalBody>
      </Modal>
    </>
  );
};

const HistoricNames = ({ team, fetchData, permissionName }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const tableColumns = useMemo(() => {
    return columns(team.historicalNames, fetchData);
  }, [team.historicalNames, fetchData]);

  const { formatMessage } = useIntl();

  return (
    <>
      <PermissionCheck permissionKeys={[permissionName]} permissionOptions={[PermissionOperations.UPDATE]}>
        <>
          <AddHistoricNameModal
            historicalNames={team.historicalNames}
            fetchData={fetchData}
            isAddModalOpen={isAddModalOpen}
            setIsAddModalOpen={setIsAddModalOpen}
          />
          <Button
            type="primary"
            onClick={() => {
              setIsAddModalOpen(true);
            }}
          >
            {formatMessage({ id: "historicalName.add" })}
          </Button>
        </>
      </PermissionCheck>
      <Table columns={tableColumns} dataSource={team.historicalNames || []}/>
    </>
  );
};

export default HistoricNames;
